import React from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HtmlWithLatex from "../../QuestionBank/components/HtmlWithLatex";
import { formatAnswerLabel } from "../../Student/utils/breakdownFormat";

const mathContentSx = {
  fontSize: "0.9rem",
  lineHeight: 1.6,
  "& .katex": { fontSize: "0.95em" },
};

export default function TutorQuestionReviewCard({ question, index }) {
  const unanswered = question.is_unanswered === true;
  const correct = !unanswered && question.is_correct === true;
  const incorrect = !unanswered && question.is_correct === false;
  const options = Array.isArray(question.options) ? question.options : [];
  const showCorrectAnswer =
    unanswered || incorrect || (question.correct_answer && question.correct_answer !== "—");

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderColor: unanswered
          ? "warning.light"
          : correct
            ? "success.light"
            : incorrect
              ? "error.light"
              : "divider",
        bgcolor: unanswered
          ? "rgba(255, 152, 0, 0.04)"
          : correct
            ? "rgba(76, 175, 80, 0.04)"
            : incorrect
              ? "rgba(244, 67, 54, 0.04)"
              : "background.paper",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Chip
          size="small"
          label={`Q${index + 1}`}
          color={unanswered ? "warning" : correct ? "success" : incorrect ? "error" : "default"}
        />
        {unanswered && (
          <Chip size="small" label="Not answered" color="warning" variant="outlined" sx={{ height: 24 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box fontWeight={600} sx={{ mb: 1, ...mathContentSx }}>
            {question.question_text ? (
              <HtmlWithLatex html={String(question.question_text)} />
            ) : (
              "—"
            )}
          </Box>
          {options.length > 0 ? (
            <Stack spacing={0.75}>
              {options.map((opt) => {
                const selected = Boolean(opt.is_selected);
                const isRight = Boolean(opt.is_correct);
                return (
                  <Box
                    key={opt.id || `${opt.letter}-${opt.text}`}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: isRight ? "success.main" : selected ? "error.main" : "divider",
                      bgcolor: isRight
                        ? "rgba(76, 175, 80, 0.08)"
                        : selected
                          ? "rgba(244, 67, 54, 0.06)"
                          : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, ...mathContentSx }}>
                      {opt.letter ? `${opt.letter}. ` : ""}
                      {opt.text ? <HtmlWithLatex html={String(formatAnswerLabel(opt.text) ?? "")} /> : "—"}
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {selected && (
                        <Chip
                          size="small"
                          label="Student answer"
                          color={isRight ? "success" : "error"}
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {isRight && !selected && (
                        <Chip
                          size="small"
                          label="Correct answer"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {isRight ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : selected ? (
                        <CancelIcon fontSize="small" color="error" />
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Stack spacing={0.5}>
              <Box sx={{ ...mathContentSx }}>
                <Typography variant="body2" component="span" fontWeight={600}>
                  Student answer:{" "}
                </Typography>
                {unanswered ? (
                  <Typography variant="body2" component="span">
                    Not answered
                  </Typography>
                ) : question.student_answer ? (
                  <HtmlWithLatex html={String(formatAnswerLabel(question.student_answer) ?? "")} />
                ) : (
                  <Typography variant="body2" component="span">
                    —
                  </Typography>
                )}
              </Box>
              {showCorrectAnswer && (
                <Box sx={{ ...mathContentSx }} color={unanswered ? "text.secondary" : "success.dark"}>
                  <Typography variant="body2" component="span" fontWeight={600}>
                    Correct answer:{" "}
                  </Typography>
                  {question.correct_answer ? (
                    <HtmlWithLatex html={String(formatAnswerLabel(question.correct_answer) ?? "")} />
                  ) : (
                    <Typography variant="body2" component="span">
                      —
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
